import React from 'react';
import {FormattedMessage, FormattedNumber} from 'react-intl';
import {toString, cloneDeep, findIndex, includes, isNumber, isString} from 'lodash';
import IntlMessageFormat from 'intl-messageformat';
import * as _i18n from '../i18n/index';
import moment from 'moment';

/**
 * Returns a translated string for the given message key and locale
 * @param id
 * @param locale
 * @param values
 * @returns {*}
 */
export const i18nString = (id, locale, values) => {
    const messages = _i18n[locale];
    if (!locale || !messages || !messages[id]) {
        console.warn(`No translation available for key "${id}" of language "${locale}"`);
        return '';
    }
    let msg = new IntlMessageFormat(messages[id], locale);
    return msg.format(values);
};

/**
 * Same as `formatNumber` but returns a String.
 * @param number
 * @param fractionDigits
 * @param locale
 */
export const formatNumberAsString = (number, {fractionDigits, locale}) => new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
}).format(number);

/**
 * Turns all values of an object into strings
 *
 * @param row
 * @returns {*}
 */
const stringifyValues = (row, lang) => {

    const roundNumbers = {
        'bid': 4,
        'ask': 4,
        'clientBid': 4,
        'clientAsk': 4,
        'traderAsk': 4,
        'traderBid': 4,
        'bidDifference': 4,
        'previousBid': 4,
        'previousAsk': 4,
        'changeAbsolute': 4,
        'changePercent': 2
    };

    Object.keys(row).forEach(key => {

        // rounding numbers
        if (includes(Object.keys(roundNumbers), key) && isNumber(row[key])){
            row[key] = formatNumberAsString(row[key], {
                locale: lang,
                fractionDigits: roundNumbers[key]
            });
        }

        // additional formatting
         // additional formatting
        if (key === "valueDate") {
            const valueDate = moment(
                row[key],
                ['DD.MM.YYYY', 'YYYY-MM-DD', moment.ISO_8601],
                true
            );
            
            const dateFormat = lang === 'de' ? 'DD.MM.YYYY' : 'YYYY-MM-DD';
            row[key] = valueDate.isValid() ? valueDate.format(dateFormat) : '';
            }
 

        row[key] = toString(row[key]);
        if (row[key] === "") {
            row[key] += "–";
        }
    });

    if (row.hasOwnProperty('ccyPair')) {
        row.ccyPair = isString(row.ccyPair) ? row.ccyPair.replace('_', ' / ') : row.ccyPair;
    }
    return row;
};

/**
 * Formats a single table element for the JSON structure created by `formatTableDataForExport`
 *
 * @param data
 * @returns {{header: string, footer: *, columns: Array, data: *}}
 */
const createTable = data => {

    /**
     * Current time and date should be available in the generated reports(pdf,csv,xls) while downloading it by using "Save as Settlement/Spot/Forward" option
     */
    //const docCreatedDate = moment(new Date()).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    const docCreatedDate = moment(new Date()).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    const dateFormat = data.lang === 'de' ? 'DD.MM.YYYY' : 'YYYY-MM-DD';
    const lastUpdateMsg = `${i18nString('dbm.table.export.lastUpdate', data.lang)}${moment(docCreatedDate).format(`${dateFormat} - HH:mm:ss [GMT]Z`)}`;
 /**
     * 
     * Defines data type and index for fields.
     * If index is not defined, the field won't be moved. If index is -1 it will be removed from fields.
     */
    const column = {
        "ccyPair": { type: "String", index: 0 },
        "label": { type: "String" },
        "ask": { type: "Float", index: 2 },
        "bid": { type: "Float", index: 1 },
        "clientAsk": { type: "Float", index: 4 },
        "clientBid": { type: "Float", index: 1 },
        "traderAsk": { type: "Float", index: 3 },
        "traderBid": { type: "Float", index: 2 },
        "bidDifference": { type: "Float", index: 5 },
        "changeAbsolute": { type: "Float", index: 3 },
        "changePercent": { type: "Float", index: 4 },
        "previousBid": { type: "Float", index: 5 },
        "previousAsk": { type: "Float", index: -1},
        "daysCount": { type: "Integer"},
        "valueDate": { type: "Date", index: 6 }
    };

    let tableData = data.data.map(obj => stringifyValues(obj, data.lang));
    let columnData = Object.keys(data.data[0]).map(col => {
        return {
            "title": i18nString(`dbm.currency.${col}`, data.lang) || col,
            "field": col,
            "type": column[col] ? column[col].type : "String"
        }
    });

    // This is where the column order is sorted.
    Object.keys(column).forEach(key => {
        let keyIndex = findIndex(columnData, col => col.field === key);

        // These should be moved to another index
        if (keyIndex !== -1 && column[key].hasOwnProperty('index')){
            columnData.splice(column[key].index, 0, columnData.splice(keyIndex, 1)[0] );
        }

        // These should not be exposed to the formatted data
        if (keyIndex !== -1 && column[key].hasOwnProperty('index') && column[key].index === -1){
            columnData.splice(keyIndex, 1);
        }
    });

    return {
        header: "",
        footer: lastUpdateMsg,
        columns: columnData,
        data: tableData
    };
};

/**
 * Returns a simple <FormattedMessage/> for internationalization
 * @param id    the message key
 * @param def   the default message
 */
export const i18n = (id, def, val) => <FormattedMessage id={id} defaultMessage={def} values={val}/>;

/**
 * Returns a formatted Number as FormattedNumber
 * @param number
 * @param fractionDigits
 */
export const formatNumber = (number, fractionDigits) => <FormattedNumber value={number}
                                                                         minimumFractionDigits={fractionDigits}
                                                                         maximumFractionDigits={fractionDigits}/>;

export const formatDate = (date, locale = 'en') => {
  if (!date) return '';
 //console.log("Before Date-",date,"Locale- ",locale);
  moment.locale(locale);

    // If already moment
    if (moment.isMoment(date)) {
        return date.format(locale === 'de' ? 'DD.MM.YYYY' : 'YYYY-MM-DD');
    }
    
    // 🔥 FIX: parse German + ISO safely
    const m = moment(date, [
        'DD.MM.YYYY',
        'YYYY-MM-DD',
        moment.ISO_8601
    ], true);
    
    if (!m.isValid()) return date;
    const afterDate = m.format(locale === 'de' ? 'DD.MM.YYYY' : 'YYYY-MM-DD');
    //console.log("After Date-",afterDate);
    return afterDate;
};

export const formatTime = (time) => moment(time).format('HH:mm:ss');

/**
 * Creates a JSON-serializable Object of table data with a structure that
 * is applicable for server-side file rendering.
 *
 * @param key (settlement, spot, forward)
 * @param title
 * @param data
 * @param lang
 * @returns {{title: *, tables: *[], language: *, createdAt: *}}
 */
export const formatTableDataForExport = ({key, title, data, lang}) => {

    let tableData = Object.assign({}, cloneDeep(data), {lang: lang});
    let formattedTables = [];
    const lastUpdateMsg = `${i18nString('dbm.table.export.lastUpdate', lang)}${data.lastUpdate}`;

    if (!tableData.data || tableData.data.length === 0) {
        return;
    }

    if ("forward" === key) {
        // Forward table structure is different. It consists of one or more tables and there has to be made some
        // adjustments here.
        tableData.data.forEach(tenorTable => {
            Object.assign(tenorTable, {
                data: tenorTable.tenors.map(t => {
                    const formattedCcyPair = tenorTable.ccyPair ? tenorTable.ccyPair.replace('_', ' / ') : '';
                    let obj = Object.assign({[formattedCcyPair]: t.label}, t);
                    delete obj.label;
                    return obj;
                })
            }, {
                lastUpdate: tableData.lastUpdate,
                lang: lang
            });
            formattedTables.push(createTable(tenorTable));
        });
    } else {
        formattedTables.push(createTable(tableData));
    }

    return {
        exportType: key,
        title: title ? title : i18nString(`dbm.stage.nav.${key}`),
        tables: formattedTables,
        language: lang,
        createdAt: lastUpdateMsg
    };
};

/**
 * Returns a short version of month like "Feb"
 *
 * @param moment
 * @param date
 * @returns {string}
 */
export const get3charMonth = (moment, date) => {
    return moment(date).format('MMM').slice(0,3);

};